/**
 * Wraps an inline predictive search bar and adds two things on top of the theme's own predictive search:
 *  - a placeholder that rotates through a list of words ("Search: One Piece", "Search: Pikachu", ...)
 *  - the products shown before the shopper types, loaded the first time the bar is focused
 *
 * The search itself (suggestions, product results, keyboard navigation) is handled by <predictive-search-component>.
 *
 * Data attributes:
 *  - data-prefix: text before the rotating word, e.g. "Search:"
 *  - data-words: JSON array of words
 *  - data-interval: seconds between words
 *  - data-fade: "true" to fade between words
 *  - data-load-empty: "true" to load the products shown before typing
 */
class RotatingSearchBar extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | undefined} */
  #rotateTimer;

  /** @type {number | undefined} */
  #swapTimer;

  #index = 0;

  #emptyStateRequested = false;

  connectedCallback() {
    this.#input = this.querySelector('input[name="q"]');
    if (!this.#input) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    this.#startRotating();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearInterval(this.#rotateTimer);
    clearTimeout(this.#swapTimer);
  }

  get #words() {
    try {
      const words = JSON.parse(this.dataset.words || '[]');
      return Array.isArray(words) ? words.map((word) => String(word).trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  get #prefix() {
    return (this.dataset.prefix ?? 'Search:').trim();
  }

  /**
   * @param {string} word
   */
  #placeholderFor(word) {
    return `${this.#prefix} ${word}`.trim();
  }

  #startRotating() {
    const words = this.#words;
    if (words.length < 2) return;

    const seconds = Number(this.dataset.interval) || 3;
    this.#index = 0;

    this.#rotateTimer = window.setInterval(() => this.#showNextWord(words), Math.max(1, seconds) * 1000);
  }

  /**
   * @param {string[]} words
   */
  #showNextWord(words) {
    const input = this.#input;
    // Nothing to swap while the tab is hidden, or once the shopper has typed something
    if (!input || document.hidden || input.value !== '') return;

    this.#index = (this.#index + 1) % words.length;
    const placeholder = this.#placeholderFor(words[this.#index] ?? '');

    const shouldFade = this.dataset.fade === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!shouldFade) {
      input.placeholder = placeholder;
      return;
    }

    // Fade the old word out (see ::placeholder in the section CSS), swap it, then fade the new one in
    input.classList.add('is-swapping');
    clearTimeout(this.#swapTimer);
    this.#swapTimer = window.setTimeout(() => {
      input.placeholder = placeholder;
      input.classList.remove('is-swapping');
    }, 250);
  }

  #handleFocusIn = () => {
    this.#input?.setAttribute('aria-expanded', 'true');
    this.#loadEmptyState();
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;

    this.#input?.setAttribute('aria-expanded', 'false');
  };

  /**
   * The theme's search clears itself on Escape; also close the dropdown by leaving the input.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') this.#input?.blur();
  };

  /**
   * The theme only preloads the "before typing" products for shoppers with recently viewed products.
   * Everyone else gets them here, on first focus, without rendering them on every page load.
   */
  #loadEmptyState() {
    if (this.dataset.loadEmpty !== 'true' || this.#emptyStateRequested) return;
    this.#emptyStateRequested = true;

    customElements.whenDefined('predictive-search-component').then(() => {
      const search = /** @type {(HTMLElement & { resetSearch?: (keepFocus?: boolean) => void }) | null} */ (
        this.querySelector('predictive-search-component')
      );
      const results = this.querySelector('[ref="predictiveSearchResults"]');

      // Already loaded (recently viewed products), or the shopper has started typing
      if (!search || !this.#input || this.#input.value !== '') return;
      if (results?.querySelector('.predictive-search-results__inner')) return;

      search.resetSearch?.(false);
    });
  }
}

if (!customElements.get('rotating-search-bar')) {
  customElements.define('rotating-search-bar', RotatingSearchBar);
}
